import { Fragment, useCallback, useMemo, useState } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { AlertTriangle, ChevronLeft, ChevronRight, Copy, Scale, Search, SlidersHorizontal, Wallet, Wand2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { MonthPicker } from '@/components/ui/month-picker'
import { useMonthCursor } from '@/hooks/useMonthCursor'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { EmptyState } from '@/components/ui/empty-state'
import { PageHeader } from '@/components/ui/page-header'
import { TableSkeleton } from '@/components/ui/skeleton'
import { MoneyInput } from '@/components/ui/money-input'
import { useCategories, buildTree } from '@/hooks/useCategories'
import { useTransactions, type TransactionRow } from '@/hooks/useTransactions'
import { useRecurringRules } from '@/hooks/useRecurring'
import { useAccounts } from '@/hooks/useAccounts'
import { useCurrentRates, convertWithRates } from '@/hooks/useCurrentRates'
import {
  useBudgets,
  useSaveBudget,
  useCopyBudget,
  useUpsertBudgets,
  useAllBudgets,
  monthKeyISO,
  PRESET_MONTH,
} from '@/hooks/useBudgets'
import { PresetDialog } from '@/components/budget/PresetDialog'
import { useT } from '@/i18n/I18nProvider'
import { useTranslateCategory } from '@/lib/categoryDisplay'
import { useHousehold } from '@/context/HouseholdContext'
import { useConfirm } from '@/context/ConfirmProvider'
import { formatCurrency } from '@/lib/currency'
import { accountIcon } from '@/lib/accountIcon'
import { monthOccurrenceStatus } from '@/lib/recurring'
import { posAt as posAtLib, fxOfMonth as fxOfMonthLib } from '@/lib/position'
import { faturaMonthOf } from '@/lib/finance'
import { cn } from '@/lib/utils'
import type { Category } from '@/types/db'

interface ConsumedItem {
  date: string
  description: string
  category: string
  account: string
  base: number
  tag: 'lançado' | 'pendente' | 'recorrente'
}

// Chave v2: o padrão passou a ser MARCADO. A chave antiga guardava a preferência
// sob o padrão desmarcado — reusá-la faria quem já tinha '0' salvo continuar
// desmarcado, e o novo padrão nunca valeria. Chave nova = começa marcado para
// todos; desmarcar continua sendo lembrado.
const INCLUDE_ROLLOVER_KEY = 'easymoney.budget.include-rollover.v2'

// Lançamentos SEM categoria não podem sumir do orçamento (o Resultado do mês
// ficaria errado). Vão para estas chaves-sentinela e aparecem numa linha própria.
const NO_CAT_EXPENSE = '__sem-categoria:expense__'
const NO_CAT_INCOME = '__sem-categoria:income__'

function monthLabel(d: Date) {
  return format(d, "MMMM 'de' yyyy", { locale: ptBR })
}
function fmtBr(iso: string) {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y}`
}
function isCurrentMonth(d: Date) {
  const n = new Date()
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth()
}
function bounds(d: Date) {
  const iso = (x: Date) => x.toISOString().slice(0, 10)
  return { from: iso(new Date(d.getFullYear(), d.getMonth(), 1)), to: iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)) }
}

export function BudgetPage() {
  const { t } = useT()
  const tc = useTranslateCategory()
  const { baseCurrency } = useHousehold()
  const [cursor, setCursor] = useMonthCursor()
  const range = useMemo(() => bounds(cursor), [cursor])
  const monthISO = useMemo(() => monthKeyISO(cursor), [cursor])

  const prevRange = useMemo(() => bounds(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1)), [cursor])
  const prevMonthISO = useMemo(() => monthKeyISO(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1)), [cursor])

  const { data: categories } = useCategories()
  const { data: monthTxs } = useTransactions(range)
  const { data: rules } = useRecurringRules()
  const { data: rates } = useCurrentRates()
  const { data: budgets } = useBudgets(monthISO)
  const { data: allBudgets } = useAllBudgets()
  const { data: accounts } = useAccounts(true)

  // INVESTIMENTOS = categorias do 3º tipo (kind 'investment'). Recebem tratamento
  // próprio: resultado COM SINAL (ganho +, perda −), fora do total de Despesas.
  const investIds = useMemo(
    () => new Set((categories ?? []).filter((c) => c.kind === 'investment').map((c) => c.id)),
    [categories],
  )

  // Orçado por mês: Map<'YYYY-MM', { inc, exp }>. Investimentos ficam FORA (o
  // resultado previsto usa o líquido REAL de day-trade, não um orçado fixo).
  const orcByMonth = useMemo(() => {
    const kindOf = new Map((categories ?? []).map((c) => [c.id, c.kind]))
    const map = new Map<string, { inc: number; exp: number }>()
    for (const b of allBudgets ?? []) {
      if (investIds.has(b.category_id)) continue
      const k = b.month.slice(0, 7)
      const cur = map.get(k) ?? { inc: 0, exp: 0 }
      const kind = kindOf.get(b.category_id)
      if (kind === 'income') cur.inc += Number(b.amount_base)
      else if (kind === 'expense') cur.exp += Number(b.amount_base)
      map.set(k, cur)
    }
    return map
  }, [allBudgets, categories, investIds])

  // Todo o histórico até o fim do mês anterior: usado para calcular a POSIÇÃO
  // REAL das contas naquele momento (a sobra herdada) e projetar meses futuros.
  const chainRange = useMemo(() => ({ from: '1900-01-01', to: prevRange.to }), [prevRange.to])
  const { data: chainTxs } = useTransactions(chainRange)
  const saveBudget = useSaveBudget()
  const copyBudget = useCopyBudget()
  const upsertBudgets = useUpsertBudgets()
  const confirm = useConfirm()

  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState(0)
  const [detail, setDetail] = useState<{ title: string; list: ConsumedItem[] } | null>(null)
  const [presetOpen, setPresetOpen] = useState(false)
  // "Incluir sobra" é uma preferência do usuário — persiste entre sessões.
  // Padrão MARCADO: só desmarca se o usuário tiver desmarcado de propósito ('0').
  const [includePrev, setIncludePrev] = useState(() => localStorage.getItem(INCLUDE_ROLLOVER_KEY) !== '0')
  function toggleIncludePrev(v: boolean) {
    setIncludePrev(v)
    localStorage.setItem(INCLUDE_ROLLOVER_KEY, v ? '1' : '0')
  }

  // Consumido por categoria (base): despesas lançadas (pagas + pendentes) +
  // recorrentes do mês ainda NÃO lançados. Guarda também os ITENS que somam.
  const { consumed, lancado, recorrente, items } = useMemo(() => {
    const nameById = new Map((categories ?? []).map((c) => [c.id, c.name]))
    const consumed = new Map<string, number>() // total (lançado + recorrente)
    const lancado = new Map<string, number>() // o que já virou transação
    const recorrente = new Map<string, number>() // recorrente do mês ainda NÃO lançado
    const items = new Map<string, ConsumedItem[]>()
    // Sem categoria vai para uma chave-sentinela (por tipo) em vez de ser
    // descartado — senão o Resultado do mês fica errado silenciosamente.
    const kindById = new Map((categories ?? []).map((c) => [c.id, c.kind]))
    const add = (
      cat: string | null | undefined,
      kind: 'income' | 'expense',
      it: Omit<ConsumedItem, 'category'>,
    ) => {
      const key = cat ?? (kind === 'income' ? NO_CAT_INCOME : NO_CAT_EXPENSE)

      // O valor entra COM SINAL RELATIVO À CATEGORIA. O `totals` soma o consumido
      // de cada categoria pelo kind DELA, então um lançamento de kind contrário
      // (uma RECEITA numa categoria de despesa) precisa ABATER, não somar.
      //
      // Isso acontece de verdade: "Resultado day-trade" vai para 'Investimentos'
      // (despesa) e pode ser lucro (income) ou prejuízo (expense). Um estorno numa
      // categoria de despesa é o mesmo caso. Antes, o lucro era somado como se
      // fosse gasto — o Resultado do Orçamento ficava 2x o valor abaixo do
      // Dashboard (que sempre somou pelo kind da TRANSAÇÃO, e estava certo).
      const catKind = cat ? (kindById.get(cat) ?? kind) : kind
      // Investimento usa o FRAME de despesa para o sinal: perda (expense) SOMA e
      // ganho (income) ABATE — assim o "consumido" da categoria = perda − ganho.
      const effKind = catKind === 'investment' ? 'expense' : catKind
      const signed = effKind === kind ? it.base : -it.base

      consumed.set(key, (consumed.get(key) ?? 0) + signed)
      const bucket = it.tag === 'recorrente' ? recorrente : lancado
      bucket.set(key, (bucket.get(key) ?? 0) + signed)
      const arr = items.get(key) ?? []
      // Guarda o valor JÁ COM SINAL: assim o total do diálogo da lupa bate com o
      // consumido da linha, e o estorno aparece como negativo (que é o que é).
      arr.push({ ...it, base: signed, category: cat ? (nameById.get(cat) ?? '—') : t('bud.noCategory') })
      items.set(key, arr)
    }
    const y = cursor.getFullYear()
    const mo = cursor.getMonth()

    // Lançamentos cujo CAIXA cai neste mês — não os que têm data neste mês.
    // Uma compra de cartão feita depois do fechamento só é paga na fatura
    // seguinte: o dinheiro sai no mês da FATURA. Contá-la no mês da compra
    // derrubava o "saldo esperado no fim do mês" por um dinheiro que ainda
    // nem saiu (foi o que o usuário viu com uma compra de 14/07 na fatura de
    // agosto). Sem fatura marcada isto é o mês da própria data — identidade
    // para todo o histórico importado, que já vem datado no vencimento.
    const mk = `${y}-${String(mo + 1).padStart(2, '0')}`
    const cashTxs = [...(chainTxs ?? []), ...(monthTxs ?? [])].filter((t) => faturaMonthOf(t) === mk)

    for (const tx of cashTxs) {
      if ((tx.kind === 'expense' || tx.kind === 'income') && !tx.transfer_group_id && tx.amount_base != null)
        add(tx.category_id, tx.kind, {
          date: tx.date,
          description: tx.description ?? t('bud.noDescription'),
          account: tx.account ? `${accountIcon(tx.account)} ${tx.account.name}` : '—',
          base: Number(tx.amount_base),
          tag: tx.status === 'pending' ? 'pendente' : 'lançado',
        })
    }
    for (const r of rules ?? []) {
      const k = r.template.kind
      if (k !== 'expense' && k !== 'income') continue
      for (const o of monthOccurrenceStatus(r, monthTxs ?? [], y, mo)) {
        if (o.included) continue
        const base = convertWithRates(r.template.amount, r.template.currency, rates)
        if (base != null)
          add(r.template.category_id, k, {
            date: o.date,
            description: r.template.description ?? t('bud.recurringPlaceholder'),
            account: r.account ? `${accountIcon(r.account)} ${r.account.name}` : '—',
            base,
            tag: 'recorrente',
          })
      }
    }
    return { consumed, lancado, recorrente, items }
  }, [monthTxs, chainTxs, rules, rates, cursor, categories, t])

  const incomeTree = useMemo(() => buildTree(categories ?? [], 'income'), [categories])
  // Despesas já NÃO incluem investimento (kind diferente); investimento em bloco próprio.
  const expenseTree = useMemo(() => buildTree(categories ?? [], 'expense'), [categories])
  const investTree = useMemo(() => buildTree(categories ?? [], 'investment'), [categories])
  const budgetOwn = (id: string) => budgets?.get(id) ?? 0
  const consumedOwn = (id: string) => consumed.get(id) ?? 0 // total, usado no Disponível/Ajuste
  const lancadoOwn = (id: string) => lancado.get(id) ?? 0 // já recebido / já gasto
  const recOwn = (id: string) => recorrente.get(id) ?? 0 // a receber / a lançar

  // Totais por tipo. *Real = TOTAL (lançado + recorrente do mês ainda não
  // lançado) — é o que entra no Resultado e o que o dashboard projeta. *Lanc =
  // só o que de fato aconteceu; *Rec = o que ainda vai cair/ser lançado.
  const totals = useMemo(() => {
    let receitaOrc = 0
    let receitaLanc = 0
    let receitaRec = 0
    let despesaOrc = 0
    let despesaLanc = 0
    let despesaRec = 0
    // Investimentos no FRAME de despesa (perda − ganho), como o `consumed` guarda.
    let investLanc = 0
    let investRec = 0
    const sum = (id: string, kind: 'income' | 'expense', isInvest: boolean) => {
      const l = lancado.get(id) ?? 0
      const r = recorrente.get(id) ?? 0
      if (isInvest) {
        investLanc += l
        investRec += r
      } else if (kind === 'income') {
        receitaLanc += l
        receitaRec += r
      } else {
        despesaLanc += l
        despesaRec += r
      }
    }
    for (const cat of categories ?? []) {
      const b = budgets?.get(cat.id) ?? 0
      if (cat.kind === 'investment') {
        sum(cat.id, 'expense', true) // frame de despesa; orçado de invest é ignorado no previsto
      } else if (cat.kind === 'income') {
        receitaOrc += b
        sum(cat.id, 'income', false)
      } else if (cat.kind === 'expense') {
        despesaOrc += b
        sum(cat.id, 'expense', false)
      }
    }
    // Sem categoria não dá para orçar, mas entra no realizado.
    sum(NO_CAT_INCOME, 'income', false)
    sum(NO_CAT_EXPENSE, 'expense', false)
    return {
      receitaOrc,
      receitaLanc,
      receitaRec,
      receitaReal: receitaLanc + receitaRec,
      despesaOrc,
      despesaLanc,
      despesaRec,
      despesaReal: despesaLanc + despesaRec,
      // Resultado de investimentos com SINAL (positivo = ganho no mês). Inverte o
      // frame de despesa (que guarda perda − ganho).
      investReal: -(investLanc + investRec),
      investLanc: -investLanc,
      investRec: -investRec,
    }
  }, [categories, budgets, lancado, recorrente, investIds])

  // Wrappers finos sobre @/lib/position — a lógica vive lá, compartilhada com o
  // relatório. Duplicá-la foi o que fez as duas telas divergirem.
  const posAt = useCallback(
    (endISO: string, txs: TransactionRow[]) => (accounts && rates ? posAtLib(endISO, txs, accounts, rates) : 0),
    [accounts, rates],
  )
  const fxOfMonth = useCallback(
    (y: number, m0: number, txs: TransactionRow[]) =>
      accounts && rates ? fxOfMonthLib(y, m0, txs, accounts, rates) : 0,
    [accounts, rates],
  )

  // Todas as transações até o fim do mês exibido (histórico + o mês em si).
  const allTxs = useMemo(() => [...(chainTxs ?? []), ...(monthTxs ?? [])], [chainTxs, monthTxs])

  // Sem o histórico completo (chainTxs) o posAt calcularia o saldo das contas sem
  // o passado — número absurdo. Só calcula quando TUDO que ele precisa chegou.
  const fxVar = useMemo(
    () =>
      chainTxs && monthTxs && accounts && rates
        ? fxOfMonth(cursor.getFullYear(), cursor.getMonth(), allTxs)
        : 0,
    [fxOfMonth, cursor, allTxs, chainTxs, monthTxs, accounts, rates],
  )

  // Resultado de um mês PARA A CADEIA (meses corrente/futuros): usa o ORÇADO,
  // não o consumido — em mês aberto as despesas ainda não foram todas lançadas,
  // e usar o consumido inflaria a sobra. Sem orçamento, cai no real. Inclui a
  // VARIAÇÃO CAMBIAL: é exatamente o que a tela mostra como resultado do mês, e
  // sem ela o mês seguinte herdaria um valor diferente do que este mês fechou.
  const resultOf = useCallback(
    (y: number, m0: number, txs: TransactionRow[]) => {
      const mk = `${y}-${String(m0 + 1).padStart(2, '0')}`
      // Régua de FATURA (faturaMonthOf), a MESMA de `cashTxs`/consumido e do
      // dashboard — senão `resultOf` (que projeta a herdada dos meses futuros)
      // divergiria da barra do Relatório quando uma parcela cai após o fechamento.
      const mTxs = txs.filter((t) => faturaMonthOf(t) === mk)
      let inc = 0
      let exp = 0
      for (const t of mTxs) {
        if (t.transfer_group_id || t.amount_base == null) continue
        if (t.kind === 'income') inc += Number(t.amount_base)
        else if (t.kind === 'expense') exp += Number(t.amount_base)
      }
      for (const r of rules ?? []) {
        const k = r.template.kind
        if (k !== 'income' && k !== 'expense') continue
        for (const o of monthOccurrenceStatus(r, mTxs, y, m0)) {
          if (o.included) continue
          const base = convertWithRates(r.template.amount, r.template.currency, rates)
          if (base == null) continue
          if (k === 'income') inc += base
          else exp += base
        }
      }
      const o = orcByMonth.get(mk)
      const incP = o && o.inc > 0.005 ? o.inc : inc
      const expP = o && o.exp > 0.005 ? o.exp : exp
      return incP - expP + fxOfMonth(y, m0, txs)
    },
    [rules, rates, orcByMonth, fxOfMonth],
  )

  // Sobra que ENTRA no mês exibido.
  //  • Mês até o corrente: é a POSIÇÃO REAL no fim do mês anterior — exata, sem
  //    âncora manual, e revaloriza junto com a cotação (fecha com o dashboard).
  //  • Mês FUTURO: parte da posição real no fim do último mês fechado e projeta,
  //    somando o resultado ORÇADO de cada mês até o anterior ao exibido.
  const prevLeftover = useMemo(() => {
    if (!chainTxs) return 0
    const now = new Date()
    const nowIdx = now.getFullYear() * 12 + now.getMonth()
    const dispIdx = cursor.getFullYear() * 12 + cursor.getMonth()
    if (dispIdx <= nowIdx) return posAt(prevRange.to, chainTxs)

    const isoOf = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    let carried = posAt(isoOf(new Date(now.getFullYear(), now.getMonth(), 0)), chainTxs) // fim do mês anterior ao atual
    let y = now.getFullYear()
    let m0 = now.getMonth()
    while (y * 12 + m0 < dispIdx) {
      carried += resultOf(y, m0, chainTxs) // resultOf recorta o mês e calcula o fx com o histórico
      m0 += 1
      if (m0 > 11) {
        m0 = 0
        y += 1
      }
    }
    return carried
  }, [chainTxs, cursor, prevRange.to, posAt, resultOf])

  function shiftMonth(delta: number) {
    setCursor((d) => new Date(d.getFullYear(), d.getMonth() + delta, 1))
  }
  function startEdit(id: string) {
    setDraft(budgetOwn(id))
    setEditing(id)
  }
  function commit(id: string) {
    saveBudget.mutate(
      { categoryId: id, month: monthISO, amount: draft },
      {
        onSuccess: () => toast.success(t('bud.toastSaved')),
        onError: (e) => toast.error(t('bud.toastSaveError'), { description: (e as Error).message }),
      },
    )
    setEditing(null)
  }
  function openDetail(title: string, catIds: string[], only?: 'lancado' | 'recorrente') {
    let list = catIds.flatMap((id) => items.get(id) ?? [])
    if (only === 'recorrente') list = list.filter((i) => i.tag === 'recorrente')
    else if (only === 'lancado') list = list.filter((i) => i.tag !== 'recorrente')
    setDetail({ title, list: list.sort((a, b) => a.date.localeCompare(b.date)) })
  }
  function setFromConsumed(id: string, amount: number) {
    // Fecha qualquer edição inline em curso: se o usuário estava digitando o
    // orçamento desta linha e clicou no valor consumido, o blur do input dispararia
    // um `commit` do rascunho concorrente com este write (mesma categoria/mês).
    setEditing(null)
    saveBudget.mutate(
      { categoryId: id, month: monthISO, amount },
      {
        onSuccess: () => toast.success(t('bud.toastSaved')),
        onError: (e) => toast.error(t('bud.toastSaveError'), { description: (e as Error).message }),
      },
    )
  }
  function copyPrev() {
    copyBudget.mutate(
      { from: prevMonthISO, to: monthISO },
      {
        onSuccess: (n) =>
          toast.success(n > 0 ? t('bud.toastCopied', { n }) : t('bud.toastCopyEmpty')),
        onError: (e) => toast.error(t('bud.toastCopyError'), { description: (e as Error).message }),
      },
    )
  }
  function applyPresets() {
    copyBudget.mutate(
      { from: PRESET_MONTH, to: monthISO },
      {
        onSuccess: (n) =>
          toast.success(
            n > 0 ? t('bud.toastPresetsApplied', { n, month: monthLabel(cursor) }) : t('bud.toastPresetsEmpty'),
          ),
        onError: (e) => toast.error(t('bud.toastApplyError'), { description: (e as Error).message }),
      },
    )
  }

  // AJUSTE: alinha o orçado ao consumido onde a realidade já passou do plano.
  //  (1) consumido sem nenhum orçamento  → cria o orçamento com o consumido.
  //  (2) consumido MAIOR que o orçado    → sobe o orçado para o consumido.
  // Nunca reduz um orçamento (consumido < orçado fica como está — é sobra).
  const pendingAdjust = useMemo(() => {
    const semOrc: { cat: Category; to: number }[] = []
    const estourou: { cat: Category; from: number; to: number }[] = []
    for (const cat of categories ?? []) {
      const c = consumed.get(cat.id) ?? 0
      const b = budgets?.get(cat.id) ?? 0
      if (c <= 0.005 || c <= b + 0.005) continue
      if (b <= 0.005) semOrc.push({ cat, to: c })
      else estourou.push({ cat, from: b, to: c })
    }
    return { semOrc, estourou, total: semOrc.length + estourou.length }
  }, [categories, consumed, budgets])

  async function runAdjust() {
    const { semOrc, estourou, total } = pendingAdjust
    if (total === 0) {
      toast.success(t('bud.toastNothingToAdjust'))
      return
    }
    const delta =
      semOrc.reduce((s, x) => s + x.to, 0) + estourou.reduce((s, x) => s + (x.to - x.from), 0)
    const ok = await confirm({
      title: t('bud.adjustConfirmTitle', { total }),
      description: (
        <span>
          {semOrc.length > 0 && (
            <>
              <b>{semOrc.length}</b> {t('bud.adjustSemOrcPre')} <b>{t('bud.adjustSemOrcBold')}</b>{' '}
              {t('bud.adjustSemOrcPost')}
              <br />
            </>
          )}
          {estourou.length > 0 && (
            <>
              <b>{estourou.length}</b> {t('bud.adjustEstourouPre')} <b>{t('bud.adjustEstourouBold')}</b>{' '}
              {t('bud.adjustEstourouPost')}
              <br />
            </>
          )}
          {t('bud.adjustTotalIncrease')} <b>{formatCurrency(delta, baseCurrency)}</b>.
        </span>
      ),
      confirmText: t('bud.adjustConfirm'),
      tone: 'warning',
    })
    if (!ok) return
    try {
      const n = await upsertBudgets.mutateAsync(
        [...semOrc.map((x) => ({ cat: x.cat, to: x.to })), ...estourou.map((x) => ({ cat: x.cat, to: x.to }))].map(
          (x) => ({ categoryId: x.cat.id, month: monthISO, amount: x.to }),
        ),
      )
      toast.success(t('bud.toastAdjusted', { n }))
    } catch (e) {
      toast.error(t('bud.toastAdjustError'), { description: (e as Error).message })
    }
  }

  function budgetCell(cat: Category) {
    if (editing === cat.id) {
      return (
        <MoneyInput
          autoFocus
          value={draft}
          onChange={setDraft}
          onBlur={() => commit(cat.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit(cat.id)
            if (e.key === 'Escape') setEditing(null)
          }}
          className="ml-auto h-8 w-28 text-right"
        />
      )
    }
    const b = budgetOwn(cat.id)
    return (
      <button className="hover:underline" onClick={() => startEdit(cat.id)}>
        {b > 0.005 ? formatCurrency(b, baseCurrency) : <span className="text-muted-foreground">{t('bud.define')}</span>}
      </button>
    )
  }

  function amountsRow(cat: Category, indent: boolean, kind: 'income' | 'expense') {
    const b = budgetOwn(cat.id)
    const c = consumedOwn(cat.id)
    const lc = lancadoOwn(cat.id) // já lançado (recebido / gasto)
    const rc = recOwn(cat.id) // recorrente do mês ainda não lançado
    const avail = kind === 'income' ? c - b : b - c
    return (
      <TableRow key={cat.id}>
        <TableCell className={cn('font-medium', indent && 'pl-8 font-normal')}>
          <span className="inline-flex items-center gap-1.5">
            {cat.icon && <span>{cat.icon}</span>}
            {tc(cat.name)}
          </span>
        </TableCell>
        <TableCell className="text-right tabular-nums whitespace-nowrap">{budgetCell(cat)}</TableCell>
        {/* REAL: o que já virou transação (recebido / gasto). */}
        <TableCell className="text-right tabular-nums whitespace-nowrap">
          {lc > 0.005 ? (
            <span className="inline-flex items-center justify-end gap-1.5">
              <button
                className="text-muted-foreground hover:text-primary"
                title={t('bud.viewPosted')}
                aria-label={t('bud.viewPosted')}
                onClick={() => openDetail(tc(cat.name), [cat.id], 'lancado')}
              >
                <Search className="size-3.5" />
              </button>
              <button
                className="hover:text-primary hover:underline"
                title={t('bud.useTotalAsBudget')}
                onClick={() => setFromConsumed(cat.id, c)}
              >
                {formatCurrency(lc, baseCurrency)}
              </button>
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        {/* PREVISTO: recorrente do mês ainda NÃO lançado. Não foi recebido/gasto.
            O número é clicável e ajusta o orçado ao total (lançado + recorrente) —
            mesmo atalho da coluna do consumido, para linhas que só têm "a lançar". */}
        <TableCell className="text-right tabular-nums whitespace-nowrap">
          {rc > 0.005 ? (
            <span className="text-muted-foreground inline-flex items-center justify-end gap-1.5">
              <button
                className="hover:text-primary"
                title={t('bud.viewRecurringNotPosted')}
                aria-label={t('bud.viewRecurringNotPosted')}
                onClick={() => openDetail(t('bud.recurringOf', { name: tc(cat.name) }), [cat.id], 'recorrente')}
              >
                <Search className="size-3.5" />
              </button>
              <button
                className="hover:text-primary hover:underline"
                title={t('bud.useTotalAsBudget')}
                onClick={() => setFromConsumed(cat.id, c)}
              >
                {formatCurrency(rc, baseCurrency)}
              </button>
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
        <TableCell
          className={cn(
            'text-right tabular-nums whitespace-nowrap',
            b < 0.005 ? 'text-muted-foreground' : avail < -0.005 ? 'text-destructive' : 'text-success',
          )}
        >
          {b > 0.005 || c > 0.005 ? formatCurrency(avail, baseCurrency) : '—'}
        </TableCell>
      </TableRow>
    )
  }

  // Linha "Sem categoria": lançamentos que não têm categoria. Não dá para orçar,
  // mas ENTRAM no Resultado — então precisam aparecer, senão o total não fecha.
  function noCatRow(kind: 'income' | 'expense') {
    const key = kind === 'income' ? NO_CAT_INCOME : NO_CAT_EXPENSE
    const c = consumed.get(key) ?? 0
    if (c <= 0.005) return null
    return (
      <TableRow className="bg-warning/5">
        <TableCell className="font-medium">
          <span className="text-muted-foreground inline-flex items-center gap-1.5">
            <AlertTriangle className="size-3.5 text-amber-500" />
            {t('bud.noCategory')}
          </span>
        </TableCell>
        <TableCell className="text-muted-foreground text-right">—</TableCell>
        <TableCell className="text-right tabular-nums whitespace-nowrap">
          <span className="inline-flex items-center justify-end gap-1.5">
            <button
              className="text-muted-foreground hover:text-primary"
              title={t('bud.viewNoCategory')}
              aria-label={t('bud.viewNoCategory')}
              onClick={() => openDetail(t('bud.noCategory'), [key])}
            >
              <Search className="size-3.5" />
            </button>
            {formatCurrency(c, baseCurrency)}
          </span>
        </TableCell>
        <TableCell className="text-muted-foreground text-right">—</TableCell>
        <TableCell className="text-muted-foreground text-right text-xs">{t('bud.categorizeToBudget')}</TableCell>
      </TableRow>
    )
  }

  function sectionTable(
    title: string,
    catTree: { parent: Category; children: Category[] }[],
    kind: 'income' | 'expense',
    realLabel: string,
    pendingLabel: string,
    thirdLabel: string,
    showNoCat = true,
  ) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="border-b px-4 py-2.5 text-sm font-semibold">{title}</div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('bud.category')}</TableHead>
                  <TableHead className="text-right">{t('bud.budget')}</TableHead>
                  <TableHead className="text-right">{realLabel}</TableHead>
                  <TableHead
                    className="text-right"
                    title={t('bud.pendingColTooltip')}
                  >
                    {pendingLabel}
                  </TableHead>
                  <TableHead className="text-right">{thirdLabel}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {catTree.map(({ parent, children }) => {
                  // Categoria SEM filhos: a linha do pai É a própria categoria, então
                  // renderiza a linha EDITÁVEL direto. A linha-resumo abaixo é só
                  // leitura — usá-la aqui deixava a categoria sem como orçar nem
                  // copiar o consumido (era o caso de Investimentos e afins).
                  if (children.length === 0) {
                    return <Fragment key={parent.id}>{amountsRow(parent, false, kind)}</Fragment>
                  }
                  const ids = [parent.id, ...children.map((c) => c.id)]
                  const aggB = ids.reduce((s, id) => s + budgetOwn(id), 0)
                  const aggC = ids.reduce((s, id) => s + consumedOwn(id), 0)
                  const aggL = ids.reduce((s, id) => s + lancadoOwn(id), 0)
                  const aggR = ids.reduce((s, id) => s + recOwn(id), 0)
                  const aggA = kind === 'income' ? aggC - aggB : aggB - aggC
                  return (
                    <Fragment key={parent.id}>
                      <TableRow className="bg-muted/40 hover:bg-muted/40">
                        <TableCell className="font-semibold">
                          <span className="inline-flex items-center gap-1.5">
                            {parent.icon && <span>{parent.icon}</span>}
                            {tc(parent.name)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium whitespace-nowrap">
                          {aggB > 0.005 ? formatCurrency(aggB, baseCurrency) : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium whitespace-nowrap">
                          {aggL > 0.005 ? (
                            <span className="inline-flex items-center justify-end gap-1.5">
                              <button
                                className="text-muted-foreground hover:text-primary"
                                title={t('bud.viewPostedGroup')}
                                aria-label={t('bud.viewPostedGroup')}
                                onClick={() => openDetail(tc(parent.name), ids, 'lancado')}
                              >
                                <Search className="size-3.5" />
                              </button>
                              {formatCurrency(aggL, baseCurrency)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-right tabular-nums whitespace-nowrap">
                          {aggR > 0.005 ? (
                            <span className="inline-flex items-center justify-end gap-1.5">
                              <button
                                className="hover:text-primary"
                                title={t('bud.viewRecurringGroup')}
                                aria-label={t('bud.viewRecurringGroup')}
                                onClick={() => openDetail(t('bud.recurringOf', { name: tc(parent.name) }), ids, 'recorrente')}
                              >
                                <Search className="size-3.5" />
                              </button>
                              {formatCurrency(aggR, baseCurrency)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell
                          className={cn(
                            'text-right tabular-nums font-medium whitespace-nowrap',
                            aggB < 0.005 ? 'text-muted-foreground' : aggA < -0.005 ? 'text-destructive' : 'text-success',
                          )}
                        >
                          {aggB > 0.005 || aggC > 0.005 ? formatCurrency(aggA, baseCurrency) : '—'}
                        </TableCell>
                      </TableRow>
                      {/* "(geral)" = os lançamentos direto no PAI, separados dos
                          filhos. Aqui sempre há filhos (sem eles, a categoria já
                          saiu como linha editável lá em cima). */}
                      {(budgetOwn(parent.id) > 0.005 || consumedOwn(parent.id) > 0.005) &&
                        amountsRow({ ...parent, name: t('bud.general', { name: tc(parent.name) }) }, true, kind)}
                      {children.map((c) => amountsRow(c, true, kind))}
                    </Fragment>
                  )
                })}
                {showNoCat && noCatRow(kind)}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    )
  }

  const roll = includePrev ? prevLeftover : 0
  // Investimentos entram com SINAL no resultado (ganho +, perda −), fora das
  // despesas. O previsto usa o líquido REAL do mês (day-trade é realizado dia a
  // dia — não dá para orçar o P&L futuro de forma confiável).
  const resultReal = totals.receitaReal - totals.despesaReal + totals.investReal + roll + fxVar
  // Projeção (orçado): usa a RECEITA ORÇADA; se não houver, cai na recebida.
  // Despesa = a ORÇADA (o plano). Dá a sobra prevista no fim do mês.
  const receitaProj = totals.receitaOrc > 0.005 ? totals.receitaOrc : totals.receitaReal
  const resultOrc = receitaProj - totals.despesaOrc + totals.investReal + roll + fxVar

  // Os números só fazem sentido com TODAS as fontes carregadas. Renderizar com
  // dados pela metade (ex.: saldo sem o histórico) mostrava valores absurdos
  // piscando antes de estabilizar.
  const ready = Boolean(
    categories && monthTxs && chainTxs && accounts && rates && budgets && allBudgets && rules,
  )

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={t('bud.title')}
        actions={
          <>
            <Button variant="outline" onClick={() => setPresetOpen(true)}>
              <SlidersHorizontal className="size-4" /> {t('bud.preset')}
            </Button>
            <Button variant="outline" onClick={applyPresets} disabled={copyBudget.isPending}>
              <Wand2 className="size-4" /> {t('bud.applyPresets')}
            </Button>
            <Button variant="outline" onClick={copyPrev} disabled={copyBudget.isPending}>
              <Copy className="size-4" /> {t('bud.copyPrev')}
            </Button>
            <Button
              variant="outline"
              onClick={runAdjust}
              disabled={upsertBudgets.isPending}
              title={t('bud.adjustTooltip', { month: monthLabel(cursor) })}
            >
              <Scale className="size-4" /> {t('bud.adjust')}
              {pendingAdjust.total > 0 && (
                <Badge variant="secondary" className="ml-1 px-1.5 text-[10px]">
                  {pendingAdjust.total}
                </Badge>
              )}
            </Button>
          </>
        }
      />

      {/* Navegador de mês */}
      <div className="flex items-center justify-between rounded-lg border p-2">
        <Button variant="ghost" size="icon" onClick={() => shiftMonth(-1)} aria-label={t('bud.prevMonth')}>
          <ChevronLeft className="size-5" />
        </Button>
        <div className="text-center">
          <MonthPicker value={cursor} onChange={setCursor} />
          <div className="text-muted-foreground text-xs">
            {t('bud.monthDespesaSummary', {
              budgeted: formatCurrency(totals.despesaOrc, baseCurrency),
              consumed: formatCurrency(totals.despesaReal, baseCurrency),
            })}
          </div>
          {!isCurrentMonth(cursor) && (
            <button className="text-primary text-xs hover:underline" onClick={() => setCursor(new Date())}>
              {t('bud.backToCurrent')}
            </button>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={() => shiftMonth(1)} aria-label={t('bud.nextMonth')}>
          <ChevronRight className="size-5" />
        </Button>
      </div>

      {!ready && (
        <Card className="border-primary/30">
          <CardContent className="p-2">
            <TableSkeleton rows={6} cols={5} />
          </CardContent>
        </Card>
      )}

      {/* Resumo do mês: receita − despesa (real e orçado) + sobra do mês anterior */}
      {ready && (
      <Card className="border-primary/30">
        <CardContent className="space-y-3 p-4">
          <div
            className={cn(
              'grid gap-3 sm:grid-cols-2 lg:flex lg:justify-between lg:gap-8',
              investIds.size > 0 ? '' : 'lg:justify-start',
            )}
          >
            <div>
              <div className="text-muted-foreground text-xs">{t('bud.summaryIncome')}</div>
              <div className="text-success text-lg font-semibold tabular-nums">
                {formatCurrency(totals.receitaLanc, baseCurrency)}
                <span className="text-muted-foreground text-xs">
                  {' / '}
                  {formatCurrency(totals.receitaOrc, baseCurrency)}
                </span>
              </div>
              {totals.receitaRec > 0.005 && (
                <div className="text-muted-foreground text-xs" title={t('bud.incomeRecTooltip')}>
                  {t('bud.plusToReceive')} <span className="tabular-nums">{formatCurrency(totals.receitaRec, baseCurrency)}</span>
                </div>
              )}
            </div>
            <div>
              <div className="text-muted-foreground text-xs">{t('bud.summaryExpense')}</div>
              <div className="text-destructive text-lg font-semibold tabular-nums">
                {formatCurrency(totals.despesaLanc, baseCurrency)}
                <span className="text-muted-foreground text-xs">
                  {' / '}
                  {formatCurrency(totals.despesaOrc, baseCurrency)}
                </span>
              </div>
            </div>
            {investIds.size > 0 && (
              <div>
                <div
                  className="text-muted-foreground text-xs"
                  title={t('bud.investTooltip')}
                >
                  {t('bud.investments')}
                </div>
                <div
                  className={cn(
                    'text-lg font-semibold tabular-nums',
                    totals.investReal < -0.005 ? 'text-destructive' : 'text-success',
                  )}
                >
                  {totals.investReal > 0.005 ? '+' : ''}
                  {formatCurrency(totals.investReal, baseCurrency)}
                </div>
                {Math.abs(totals.investRec) > 0.005 && (
                  <div className="text-muted-foreground text-xs">
                    {t('bud.includesToPost')} <span className="tabular-nums">{formatCurrency(totals.investRec, baseCurrency)}</span>
                  </div>
                )}
              </div>
            )}
            <div>
              <div className="text-muted-foreground text-xs" title={t('bud.resultTooltip')}>
                {t('bud.resultLabel')}{includePrev ? t('bud.plusRollover') : ''}
              </div>
              <div className="flex items-baseline gap-1">
                <span
                  className={cn(
                    'text-lg font-semibold tabular-nums',
                    resultReal < -0.005 ? 'text-destructive' : 'text-success',
                  )}
                >
                  {formatCurrency(resultReal, baseCurrency)}
                </span>
                <span
                  className={cn('text-sm tabular-nums', resultOrc < -0.005 ? 'text-destructive' : 'text-muted-foreground')}
                >
                  {' / '}
                  {formatCurrency(resultOrc, baseCurrency)}
                </span>
              </div>
              {Math.abs(fxVar) > 0.005 && (
                <div
                  className="text-muted-foreground mt-0.5 text-xs"
                  title={t('bud.fxTooltip')}
                >
                  {t('bud.includesFx')}{' '}
                  <span className={cn('tabular-nums', fxVar < 0 ? 'text-destructive' : 'text-success')}>
                    {fxVar > 0 ? '+' : ''}
                    {formatCurrency(fxVar, baseCurrency)}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox checked={includePrev} onCheckedChange={(v) => toggleIncludePrev(v === true)} />
              {t('bud.includeRollover')}
            </label>
            <span className="font-medium tabular-nums">{formatCurrency(prevLeftover, baseCurrency)}</span>
          </div>
        </CardContent>
      </Card>
      )}

      {ready && incomeTree.length === 0 && expenseTree.length === 0 && investTree.length === 0 ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={Wallet}
              title={t('bud.emptyTitle')}
              description={t('bud.emptyDesc')}
            />
          </CardContent>
        </Card>
      ) : (
        <>
          {ready &&
            sectionTable(t('bud.income'), incomeTree, 'income', t('bud.received'), t('bud.toReceiveCol'), t('bud.difference'))}
          {ready &&
            sectionTable(t('bud.expenses'), expenseTree, 'expense', t('bud.consumed'), t('bud.toPost'), t('bud.available'))}
          {/* Investimentos NÃO têm tabela: não há planejamento/orçado — só o
              resultado líquido do mês, mostrado no resumo acima (com sinal). */}
        </>
      )}

      <Dialog open={detail != null} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="flex max-h-[85svh] flex-col overflow-hidden sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="truncate">{detail?.title}</DialogTitle>
            <DialogDescription>
              {t('bud.detailSummary', {
                count: detail?.list.length ?? 0,
                month: monthLabel(cursor),
                total: formatCurrency(detail?.list.reduce((s, i) => s + i.base, 0) ?? 0, baseCurrency),
              })}
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const multiCat = new Set((detail?.list ?? []).map((i) => i.category)).size > 1
            return (
              <div className="divide-border divide-y overflow-y-auto pr-4">
                {(detail?.list ?? []).map((it, i) => (
                  <div key={i} className="hover:bg-accent/40 flex items-start gap-3 rounded px-2 py-2.5 text-sm">
                    <span className="text-muted-foreground mt-0.5 w-[62px] shrink-0 text-xs tabular-nums">
                      {fmtBr(it.date)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate">{it.description}</span>
                        {it.tag !== 'lançado' && (
                          <Badge
                            variant="outline"
                            className={cn(
                              'shrink-0 text-[10px]',
                              it.tag === 'pendente' ? 'text-amber-600 dark:text-amber-400' : 'text-primary',
                            )}
                          >
                            {it.tag === 'pendente' ? t('bud.tagPending') : t('bud.tagRecurring')}
                          </Badge>
                        )}
                      </div>
                      <div className="text-muted-foreground truncate text-xs">
                        {multiCat ? `${tc(it.category)} · ${it.account}` : it.account}
                      </div>
                    </div>
                    <span className="mt-0.5 shrink-0 tabular-nums whitespace-nowrap">
                      {formatCurrency(it.base, baseCurrency)}
                    </span>
                  </div>
                ))}
              </div>
            )
          })()}
        </DialogContent>
      </Dialog>

      <PresetDialog open={presetOpen} onOpenChange={setPresetOpen} />
    </div>
  )
}
